import {
	CLEARABLE_CLINIC_SETTINGS_FIELDS,
	ClearableClinicSettingsField,
	UpdateClinicSettingsRequest,
} from "../../../types/finance/FinanceTypes";

/**
 * The pure half of `InvoicingSettingsTab` — turning the antd form's values into a `PUT` body.
 *
 * Extracted so the clearing rule below can be pinned by a test that renders nothing.
 */

/**
 * Strip the separators people actually type into an identifier field.
 *
 * An ABN is quoted `12 345 678 901` and a BSB `123-456`, and both are stored bare. The server
 * strips them too (a `@Transform` that runs BEFORE `@Matches`), so this is a convenience — but
 * doing it here as well means the field's own rule is checked against the value that will be
 * stored, so an operator is never told "abn must be 11 digits" about a value that has 11 digits
 * and two spaces.
 */
export function stripSeparators(value: unknown): string {
	return typeof value === "string" ? value.replace(/[\s-]/g, "") : "";
}

/** The fields whose separators are stripped on the way out. */
const STRIPPED_FIELDS = new Set(["abn", "bankBsb", "bankAccountNumber"]);

const CLEARABLE = new Set<string>(CLEARABLE_CLINIC_SETTINGS_FIELDS);

/**
 * Form values -> `PUT /clinic-settings` body.
 *
 * ── Emptying a field CLEARS it ───────────────────────────────────────────────────────────────
 *
 * The first cut of this dropped every blank key, which meant a clinic could set an ABN but never
 * un-set one: clearing the input sent nothing, the server's merge left the old value, and the
 * field silently repopulated on the next load. That is worse than not offering the field, because
 * a stale ABN keeps getting printed on documents headed "Tax Invoice".
 *
 * So a blank on a NULLABLE field now sends an explicit `null`:
 *
 *   `null` rather than `""` because `@IsOptional()` short-circuits every other validator for
 *   null — `""` is a 400 on `abn`, `bankBsb` and `contactEmail`, and on the remaining fields it
 *   would store an empty string instead of clearing the column;
 *
 *   and only for the nullable columns, because `legalName`, `gstRegistered`, the numbering and
 *   the invoicing defaults are NOT NULL — a `null` there is a 500, not a clear. Blanks on those
 *   are omitted, leaving the stored value alone.
 *
 * `legalName` is always sent: it is the one required field and the create case needs it.
 */
export function buildClinicSettingsBody(
	values: Record<string, unknown>,
): UpdateClinicSettingsRequest {
	const body: Record<string, unknown> = { legalName: values.legalName };

	Object.keys(values).forEach((key) => {
		if (key === "legalName") {
			return;
		}

		const raw = values[key];
		const value = STRIPPED_FIELDS.has(key) && typeof raw === "string" ? stripSeparators(raw) : raw;
		const isBlank = value === undefined || value === null || value === "";

		if (!isBlank) {
			body[key] = value;
			return;
		}

		if (CLEARABLE.has(key)) {
			// Explicitly clear a nullable column the operator emptied.
			body[key] = null;
			return;
		}

		// A NOT NULL column left blank: omit the key so the stored value survives.
	});

	return body as unknown as UpdateClinicSettingsRequest;
}

/** True when `field` is a nullable column an operator may clear. Exported for the test. */
export function isClearableField(field: string): field is ClearableClinicSettingsField {
	return CLEARABLE.has(field);
}
